// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — how close a globe's camera may come (decision CR-D3, "zoom by data
 * resolution"). The ceiling is 8, the GIBS imagery's last level; it rises to
 * 9.5 while the globe shows something sharp enough to be worth it: an own
 * vector layer (Situation room, CR5), or a dataset whose grid is finer than
 * {@link FINE_GRID_METRES}, such as Landsat surface temperature over a city.
 * It never reaches 10, where MapLibre's globe turns into Mercator (owner,
 * 2026-10-03). The one exception is the city close-up (for everyone since
 * 2026-10-18, off with `?cityPreview=0`): its globe stays a sphere at every
 * zoom, so over a city pack the ceiling is {@link CITY_MAX_ZOOM}, and
 * {@link CITY_DEEP_MAX_ZOOM} over its zoom 16–17 photos (see cityCloseup.ts).
 *
 * Each reason is held per map, so an own layer and a fine dataset can come
 * and go in any order without one lowering the ceiling the other raised.
 */

import type { Map as MaplibreMap } from 'maplibre-gl'
import type { DatasetOverlayOptions } from '../types'
import { CITY_DEEP_MAX_ZOOM, CITY_MAX_ZOOM } from './cityCloseup'

/** The ceiling with nothing fine on the globe. */
export const GLOBE_MAX_ZOOM = 8
/** The ceiling while something fine is shown: under 10, where the globe would become Mercator. */
export const FINE_DATA_MAX_ZOOM = 9.5
/** A dataset whose frame pixel is this fine or finer (in its coarser direction) lets the camera come closer. */
export const FINE_GRID_METRES = 200

/**
 * Why a globe may come closer. `cityPack` is held only by a globe whose
 * projection stays a sphere at every zoom (the city close-up preview), and
 * `cityDeep` by such a globe over a pack's zoom 16–17 photos.
 */
export type FineReason = 'ownLayers' | 'dataset' | 'cityPack' | 'cityDeep'

/** The ceiling each reason allows. */
const CEILING: Record<FineReason, number> = {
  ownLayers: FINE_DATA_MAX_ZOOM,
  dataset: FINE_DATA_MAX_ZOOM,
  cityPack: CITY_MAX_ZOOM,
  cityDeep: CITY_DEEP_MAX_ZOOM,
}

const METRES_PER_DEGREE = 111_320

const reasons = new WeakMap<MaplibreMap, Set<FineReason>>()

/**
 * Turn one reason for the closer ceiling on or off, and set the map's
 * ceiling to match. A camera above a lowered ceiling is brought down to it:
 * MapLibre's globe (5.x) keeps the old zoom when the ceiling drops.
 */
export function setFineReason(map: MaplibreMap, reason: FineReason, on: boolean): void {
  let held = reasons.get(map)
  if (!held) {
    held = new Set()
    reasons.set(map, held)
  }
  if (on) held.add(reason)
  else held.delete(reason)
  let ceiling = GLOBE_MAX_ZOOM
  for (const r of held) ceiling = Math.max(ceiling, CEILING[r])
  map.setMaxZoom(ceiling)
  dropStaleCameraCopy(map)
  if (map.getZoom() > ceiling) map.easeTo({ zoom: ceiling, duration: 400 })
}

/**
 * MapLibre 5.21 works on a copy of the camera (`_requestedCameraState`)
 * while a `transformCameraUpdate` is set (the city close-up keeps the
 * camera above the ground with one, sphereCloseupFix.ts). On its globe that
 * copy's `setMaxZoom` / `setMaxPitch` does not reach the copy's inner
 * sphere camera, so a jump right after a raised ceiling was clamped to the
 * old one (zoom 16 asked, 8 given; 2026-10-18). Dropping the copy after a
 * limit changes makes the next move copy the live camera, which is right.
 * MapLibre 6 keeps the copy on the map's camera (`_camera`).
 */
export function dropStaleCameraCopy(map: MaplibreMap): void {
  const m = map as unknown as { _requestedCameraState?: unknown; _camera?: { _requestedCameraState?: unknown } }
  delete m._requestedCameraState
  if (m._camera) delete m._camera._requestedCameraState
}

/**
 * The size on the ground of one frame pixel, in metres, in its coarser
 * direction (east–west at the box's middle latitude, or north–south);
 * null when the frame's size is not known yet. A dataset without a box
 * covers the whole globe.
 */
export function frameGridMetres(
  frame: { width: number; height: number },
  options?: Pick<DatasetOverlayOptions, 'boundingBox' | 'cropRect'> | null,
): number | null {
  const crop = options?.cropRect
  const mapWidth = frame.width * (crop?.us ?? 1)
  const mapHeight = frame.height * (crop?.vs ?? 1)
  if (!(mapWidth > 0) || !(mapHeight > 0)) return null
  const box = options?.boundingBox ?? { n: 90, s: -90, w: -180, e: 180 }
  const lonSpan = box.w <= box.e ? box.e - box.w : box.e + 360 - box.w
  const latSpan = box.n - box.s
  if (!(lonSpan > 0) || !(latSpan > 0)) return null
  const midLat = ((box.n + box.s) / 2) * Math.PI / 180
  const eastWest = (lonSpan / mapWidth) * METRES_PER_DEGREE * Math.cos(midLat)
  const northSouth = (latSpan / mapHeight) * METRES_PER_DEGREE
  return Math.max(eastWest, northSouth)
}

/** True when the frame is fine enough for the closer ceiling. */
export function isFineGrid(
  frame: { width: number; height: number },
  options?: Pick<DatasetOverlayOptions, 'boundingBox' | 'cropRect'> | null,
): boolean {
  const metres = frameGridMetres(frame, options)
  return metres !== null && metres <= FINE_GRID_METRES
}
